import { useState, useEffect, useCallback } from 'react';
import useSlingApi from '@sdk/api/useSlingApi';
import extendDocumentItem from '@sdk/users/utils/extendDocumentItem';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import { usePersonaCollection } from '@sdk/personaCollection/PersonaCollectionContext';
import type { DocumentData } from '@sdk/users/types/DocumentData';
import type { User } from '@sdk/users/types/User';

export const useUserDocumentsView = (
  user: User,
  documentId: number | string,
  setDocumentIdInPath: (value: number | string | undefined) => void,
  disabled: boolean
) => {
  const api = useSlingApi();
  const { showErrorToast } = useToast();
  const [documentToEdit, setDocumentToEdit] = useState<DocumentData>();
  const [isListLoading, setIsListLoading] = useState(true);
  const [documentsList, setDocumentsList] = useState<DocumentData[]>();
  const { personasDict } = usePersonaCollection();

  const goBackToList = (): void => {
    setDocumentIdInPath(undefined);
    getDocumentsList();
  };

  const onEdit = (document: DocumentData) => {
    setDocumentIdInPath(document.id);
    setDocumentToEdit(document);
  };

  const onAdd = () => {
    setDocumentIdInPath('new');
    setDocumentToEdit(null);
  };

  const goToDocument = useCallback(
    (id: number | string, list = documentsList): void => {
      if (!list) {
        return;
      }

      const doc = list.find((item: DocumentData) => item.id === id);
      if (doc) {
        setDocumentToEdit(doc);
      }
    },
    [documentsList]
  );

  const getDocumentsList = async () => {
    if (disabled) {
      setIsListLoading(false);
      return;
    }

    setIsListLoading(true);

    try {
      const { data } = await api.users.getDocumentsList(user);
      const mapped = data.map((item) => extendDocumentItem(item, personasDict));
      setDocumentsList(mapped);

      if (documentId) {
        goToDocument(documentId, mapped);
      }
    } catch (e) {
      showErrorToast(getErrorForToast(e));
    } finally {
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    getDocumentsList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    goToDocument(documentId);
  }, [goToDocument, documentId]);

  return {
    isListLoading,
    documentsList,
    getDocumentsList,
    documentToEdit,
    setDocumentToEdit,
    goBackToList,
    onEdit,
    onAdd,
  };
};
